import React, { useContext, useState } from 'react';
import { Container, InputGroup, FormControl, Button, Spinner } from 'react-bootstrap';
import ReportsService from '../../../../../../services/reports-service';
import { IdentityContext } from '../../../../../../context/identity-context';
import { useSortableData } from './components/hooks/use-sortable-data/useSortableData';
import ReportTable from './components/report-table/report-table';
import { UploadModal, InfoModal, CommentModal, CreateReportModal } from './components/modal/modal';
import { ExcelPreviewModal } from './components/modal/modal';
import * as XLSX from 'xlsx';

const MAX_PREVIEW_ROWS = 500; // limit rows for performance

const CorporateActionPaymentReportContainer = ({ items }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [openRows, setOpenRows] = useState([]);
  const [checkedReportIndexes, setCheckedReportIndexes] = useState(new Set());
  const [uploadModalData, setUploadModalData] = useState({ show: false, report: null, item: null });
  const [generateModalData, setGenerateModalData] = useState({ show: false, message: '', isSuccess: false });
  const [sendModalData, setSendModalData] = useState({ show: false, message: '' });
  const [commentModalData, setCommentModalData] = useState({ show: false, comment: '' });
  const [sendingReports, setSendingReports] = useState(false);

  // Excel preview modal states
  const [excelPreviewData, setExcelPreviewData] = useState([]);
  const [showExcelPreviewModal, setShowExcelPreviewModal] = useState(false);
  // per-item loading state for preview
  const [previewLoadingMap, setPreviewLoadingMap] = useState({});

  // Create Report modal states
  const [createModalShow, setCreateModalShow] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({ address: '' });
  const [createReportModalData, setCreateReportModalData] = useState({ show: false, report: null, item: null });

  const { user } = useContext(IdentityContext);
  const reportService = new ReportsService();
  const { sortedItems, sortConfig, handleSort } = useSortableData(items);

  const handleCreateReport = (report, item) => {
    setCreateModalShow(true);
    setCreateReportModalData({ show: true, report, item });
  };
  const handleCreateModalClose = () => {
    setCreateModalShow(false);
  };
  const handleFormSubmit = async (data, report, item) => {
    setIsSubmitting(true);
    const { address } = data;
    const makerName = user.attributes['custom:display_name'];
    const reportTitle = report.title;
    const securityIssuer = item.issuer;
    const paymentDate = item.paymentDate;
    const isin = item.isin;

    try {
      const response = await reportService.caMakerCreateCreditLetter(
        isin, reportTitle, securityIssuer, makerName, paymentDate, address
      );
      console.log('Report created successfully:', response);
      setCreateModalShow(false);
      window.location.reload();
    } catch (error) {
      console.error('Error during form submission:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRowClick = (itemId) => {
    setOpenRows(prev =>
      prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId]
    );
  };

  const handleCheckboxChange = (itemId, reportIndex, isChecked) => {
    const key = `${itemId}-${reportIndex}`;
    setCheckedReportIndexes(prev => {
      const newSet = new Set(prev);
      isChecked ? newSet.add(key) : newSet.delete(key);
      return newSet;
    });
  };

  const handleUploadClick = (report, item) => {
    setUploadModalData({ show: true, report, item });
  };
  const handleUploadModalClose = () => setUploadModalData({ show: false, report: null, item: null });
  const handleCommentClick = (comment) => setCommentModalData({ show: true, comment });
  const handleCommentModalClose = () => setCommentModalData({ show: false, comment: '' });

  const canGenerateReports = () => {
    return Array.from(checkedReportIndexes).every((key) => {
      const [itemId, reportIndex] = key.split('-');
      const selectedItem = items.find(item => item.id.toString() === itemId);
      const selectedReport = selectedItem.reports[reportIndex];
      if (selectedReport.title === "Entitlement Report Excel Version") {
        return false;
      }
      return ['Report Available', 'Not Available'].includes(selectedReport.availability);
    });
  };

  const canSendReports = () => {
    return Array.from(checkedReportIndexes).every((key) => {
      const [itemId, reportIndex] = key.split('-');
      const selectedItem = items.find(item => item.id.toString() === itemId);
      const selectedReport = selectedItem.reports[reportIndex];
      return ['Report Available', 'For Adjustment'].includes(selectedReport.availability);
    });
  };

  const getEmbedUrl = (url) => {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname.toLowerCase();
      if (pathname.endsWith('.xlsx') || pathname.endsWith('.xls')) {
        return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
      }
    } catch (e) {
      const strippedUrl = url.split('?')[0].toLowerCase();
      if (strippedUrl.endsWith('.xlsx') || strippedUrl.endsWith('.xls')) {
        return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
      }
    }
    return url;
  };

  // Helper: normalize and limit sheet data
  const normalizeSheet = (sheet) => {
    const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
    const totalRows = jsonData.length;
    let usedRows = jsonData;
    if (totalRows > MAX_PREVIEW_ROWS) {
      usedRows = jsonData.slice(0, MAX_PREVIEW_ROWS);
      console.warn(`Preview limited to first ${MAX_PREVIEW_ROWS} rows out of ${totalRows}`);
      // Optionally: you could store totalRows somewhere to show in modal
    }
    const maxCols = usedRows.reduce((max, row) => Math.max(max, row.length), 0);
    const normalizedData = usedRows.map(row =>
      Array.from({ length: maxCols }, (_, i) => (row[i] !== undefined ? row[i] : ''))
    );
    return normalizedData.map(row =>
      row.map(cell => ({ value: cell, readOnly: true }))
    );
  };

  const handlePreviewClick = async (report, item) => {
    const key = item.id; // assume id unique
    // set loading for this item
    setPreviewLoadingMap(prev => ({ ...prev, [key]: true }));
    try {
      const response = await reportService.retrieveReport(
        report.reportType,
        item.isin,
        report.title,
        item.paymentDate
      );
      const fileURL = response.fileURL;
      if (!fileURL) {
        throw new Error('No file URL returned');
      }
      let isExcelExt = false;
      try {
        const urlObj = new URL(fileURL);
        const path = urlObj.pathname.toLowerCase();
        if (path.endsWith('.xlsx') || path.endsWith('.xls')) {
          isExcelExt = true;
        }
      } catch (_) {
        const lower = fileURL.split('?')[0].toLowerCase();
        if (lower.endsWith('.xlsx') || lower.endsWith('.xls')) {
          isExcelExt = true;
        }
      }

      if (isExcelExt) {
        // Try fetching & parsing
        try {
          const fileResponse = await fetch(fileURL);
          if (!fileResponse.ok) throw new Error(`Network response was not ok (${fileResponse.status})`);
          const blob = await fileResponse.blob();
          const arrayBuffer = await blob.arrayBuffer();
          const workbook = XLSX.read(arrayBuffer, { type: 'array' });
          if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
            throw new Error('No sheets found in workbook');
          }
          // For now pick first sheet; could extend to allow user to pick
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          const data = normalizeSheet(sheet);
          setExcelPreviewData(data);
          setShowExcelPreviewModal(true);
        } catch (parseErr) {
          console.error('Excel fetch/parse failed, falling back to open:', parseErr);
          const embedUrl = getEmbedUrl(fileURL);
          const newWindow = window.open(embedUrl, '_blank', 'noopener,noreferrer');
          if (newWindow) newWindow.opener = null;
        }
      } else {
        // non-Excel: open directly (or via embed if Office viewer)
        const embedUrl = getEmbedUrl(fileURL);
        const newWindow = window.open(embedUrl, '_blank', 'noopener,noreferrer');
        if (newWindow) newWindow.opener = null;
      }
    } catch (error) {
      console.error('Error fetching report preview:', error);
      // Optionally show a toast/modal here
    } finally {
      // clear loading
      setPreviewLoadingMap(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const handleDownloadClick = async (report, item) => {
    try {
      const response = await reportService.retrieveReport(
        report.reportType, item.isin, report.title, item.paymentDate
      );
      const url = response.fileURL;
      if (!url) throw new Error('No file URL returned');
      const filename = response.fileName || url.substring(url.lastIndexOf('/') + 1);
      const fileResponse = await fetch(url);
      if (!fileResponse.ok) throw new Error('Network response was not ok');
      const blob = await fileResponse.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error('Error downloading report:', error);
      // Optionally show error feedback
    }
  };

  const handleGenerateClick = () => {
    const checkedCount = checkedReportIndexes.size;
    if (checkedCount > 0) {
      let allSuccess = true;
      checkedReportIndexes.forEach((key) => {
        const [itemId, reportIndex] = key.split('-');
        const selectedItem = items.find(item => item.id.toString() === itemId);
        const selectedReport = selectedItem.reports[reportIndex];
        reportService.generateReport(
          selectedReport.reportType,
          selectedReport.title,
          selectedItem.isin,
          selectedItem.paymentDate
        ).catch(() => { allSuccess = false; });
      });
      const message = allSuccess
        ? `Reports Successfully Generated\n${checkedCount} reports have been successfully generated.`
        : 'There was an issue generating some reports.';
      setGenerateModalData({ show: true, message, isSuccess: allSuccess });
    } else {
      setGenerateModalData({
        show: true,
        message: 'No Reports Generated\nThere are no reports generated for selected items.',
        isSuccess: false
      });
    }
  };

  const handleSendClick = () => {
    setSendingReports(true);
    const makerName = user.attributes['custom:display_name'];
    const checkedCount = checkedReportIndexes.size;
    let successCount = 0;
    if (checkedCount > 0) {
      const sendPromises = Array.from(checkedReportIndexes).map((key) => {
        const [itemId, reportIndex] = key.split('-');
        const selectedItem = items.find(item => item.id.toString() === itemId);
        const selectedReport = selectedItem.reports[reportIndex];
        if (selectedReport && ['Report Available', 'For Adjustment'].includes(selectedReport.availability)) {
          return reportService.sendReport(
            selectedItem.isin,
            selectedReport.title,
            selectedReport.reportType,
            selectedItem.paymentDate,
            makerName
          ).then(() => { successCount++; })
            .catch(error => console.error('Error sending report:', error));
        }
        return Promise.resolve();
      });
      Promise.all(sendPromises).then(() => {
        setSendingReports(false);
        const message = successCount === checkedCount
          ? `Reports Successfully Sent\n${checkedCount} reports have been successfully sent.`
          : `Some reports were not sent. Successfully sent ${successCount} out of ${checkedCount}.`;
        setSendModalData({ show: true, message });
      });
    } else {
      setSendModalData({
        show: true,
        message: 'No Reports Sent\nThere are no reports sent for selected items.'
      });
      setSendingReports(false);
    }
  };

  const handleGenerateModalClose = () => {
    setGenerateModalData({ show: false, message: '', isSuccess: false });
    if (generateModalData.isSuccess) window.location.reload();
  };

  const handleSendModalClose = () => {
    setSendModalData({ show: false, message: '' });
    setSendingReports(false);
    if (sendModalData.message.includes('Successfully Sent')) window.location.reload();
  };

  return (
    <Container className="report-container">
      <InputGroup className="mb-3">
        <FormControl
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </InputGroup>

      <ReportTable
        items={sortedItems}
        searchTerm={searchTerm}
        openRows={openRows}
        onRowClick={handleRowClick}
        checkedReportIndexes={checkedReportIndexes}
        onCheckboxChange={handleCheckboxChange}
        onUploadClick={handleUploadClick}
        onPreviewClick={handlePreviewClick}
        onDownloadClick={handleDownloadClick}
        onCommentClick={handleCommentClick}
        onCreateReport={handleCreateReport}
        sortConfig={sortConfig}
        onSort={handleSort}
        // Pass previewLoadingMap so ReportTable can disable/show spinner on the right row:
        previewLoadingMap={previewLoadingMap}
      />

      <div className="action-buttons">
        <Button
          variant="secondary"
          className="me-2"
          onClick={handleGenerateClick}
          disabled={!canGenerateReports()}
        >
          Generate Reports
        </Button>
        <Button
          variant="primary"
          onClick={handleSendClick}
          disabled={!canSendReports() || sendingReports}
        >
          {sendingReports ? (
            <Spinner animation="border" style={{ width: "20px", height: "20px" }} />
          ) : "Send Reports"}
        </Button>
      </div>

      <UploadModal
        report={uploadModalData.report}
        item={uploadModalData.item}
        show={uploadModalData.show}
        onClose={handleUploadModalClose}
      />
      <InfoModal
        show={generateModalData.show}
        title="Generate Report"
        message={generateModalData.message}
        onClose={handleGenerateModalClose}
      />
      <InfoModal
        show={sendModalData.show}
        title="Send Report"
        message={sendModalData.message}
        onClose={handleSendModalClose}
      />
      <CommentModal
        show={commentModalData.show}
        comment={commentModalData.comment}
        onClose={handleCommentModalClose}
      />

      {showExcelPreviewModal && (
        <ExcelPreviewModal
          show={showExcelPreviewModal}
          data={excelPreviewData}
          onClose={() => {
            setShowExcelPreviewModal(false);
            setExcelPreviewData([]); // clear data on close
          }}
        />
      )}

      <CreateReportModal
        show={createModalShow}
        onClose={handleCreateModalClose}
        onSubmit={handleFormSubmit}
        formData={formData}
        setFormData={setFormData}
        report={createReportModalData.report}
        item={createReportModalData.item}
        isSubmitting={isSubmitting}
      />
    </Container>
  );
};

export default CorporateActionPaymentReportContainer;
